import fse, { pathExists } from 'fs-extra';
import { Common } from '../common';
import { exec } from 'child_process';

/**
 * Represents the type of upgrade that needs to be performed.
 */
export enum UpgradeType {
    WithBaseConfig = 'WithBaseConfig',
    WithoutBaseConfig = 'WithoutBaseConfig',
    NoOperation = 'NoOperation'
}

/**
 * Represents the messages that are displayed during the upgrade process.
 */
export enum EslintUpgraderMessages {
    StartingUpgrade = 'Starting upgrade eslinting process...',
    CheckingFilePaths = 'Checking file paths...',
    UpdateTypeWithBaseConfig = 'UpdateType: WithBaseConfig',
    UpdateTypeWithoutBaseConfig = 'UpdateType: WithoutBaseConfig',
    UpdateTypeWithoutBoth = 'UpdateType: NoOperation',
    ExitWithoutBaseConfig = 'Exiting upgrade eslinting process...',
    RCANoTsConfigFile = 'Please manually create a tsconfig.json file in the root of your project.',
    GettingTsConfigFile = 'Getting tsconfig.json file...',
    UpdateTsConfigContent = 'Updating tsconfig.json content...',
    RevertTsConfigContent = 'Reverting tsconfig.json content...',
    RenameTsBaseConfig = 'Renaming tsconfig.base.json to tsconfig.base.json_backup...',
    RevertTsBaseConfig = 'Renaming tsconfig.base.json_backup to tsconfig.base.json...',
    UpdateEslintConfig = 'Updating .eslintrc.json content...',
    UpdatePolyfills = 'Updating polyfills.ts content...',
    UpdateKarmaFile = 'Updating karma.conf.js content...',
    FinalizeInfoLintFix = `There are eslint violations that needs fix... After fixing the violations, please run the following commands:\n
        npx eslint . --fix \n
        gulp lintApp \n
        gulp build \n
    `,
    Completed = 'Upgrade completed!'
}

/**
 * This is the main class of the EslintUpgrader tool.
 * It is responsible for upgrading the Tslint to Eslint.
 */
export class EslintUpgrader {
    private tsconfigBaseFilePath: string;
    private tsconfigFilePath: string;
    private angularFilePath: string;

    private debug = false;
    private libUpgrade = false;

    private readonly tsconfigBaseFileName = 'tsconfig.base.json';
    private readonly tsconfigFileName = 'tsconfig.json';
    private readonly tsconfigBaseFileNameBackUp = 'tsconfig.base.json_backup';
    private readonly angularFileName = 'angular.json';

    private readonly eslintConfigFileName = '.eslintrc.json';
    private readonly eslintConfigFileContent = {
        "extends": "./node_modules/@microsoft/windows-admin-center-sdk/tools/code-formatter/.eslintrc.base.json"
    };
    private readonly polifillsFilePath = './src/polyfills.ts';
    private readonly karmaConfigFilePath = './src/karma.conf.js';
    private readonly eslintDisable = 'eslint-disable'
    private readonly targetGulpLintFilePath = '.\\gulpfile.ts\\common\\lint.ts';
    private readonly targetGulpLintIndexFilePath = './gulpfile.ts/index.ts';
    private readonly targetGulpLintUiTestAutomationFilePath = '.\\gulpfile.ts\\common\\ui-test-automation.ts';

    /**
     * The main upgrade process
     * @param debug - Indicates if the debug mode is enabled.
     * @returns Promise<void> - Returns a promise that resolves when the upgrade process is completed.
     */
    public async upgrade(debug = false): Promise<void> {
        this.debug = !!debug;

        this.logger(EslintUpgraderMessages.StartingUpgrade);
        this.logger(EslintUpgraderMessages.CheckingFilePaths);

        const upgradeType = this.checkFileExists();
        this.checkLibUpgrade();

        switch (upgradeType) {
            case UpgradeType.WithBaseConfig:
                this.logger(EslintUpgraderMessages.UpdateTypeWithBaseConfig);

                this.updateTsConfigContent();
                this.renameTsBaseConfig();
                await this.addEslintSchematics();
                await this.esLintSchematicsRunModuleApp();

                if (this.libUpgrade) {
                    await this.esLintSchematicsRunModuleLib();
                }

                await this.uninstallDependencies();
                await this.installDependencies();
                await this.replaceEslintConfig();
                this.updateTsConfigContent(true);
                this.renameTsBaseConfig(true);
                await this.replaceGulpLintFile();
                this.cleanUpPolyfills();
                this.cleanUpKarmaConfigFile();
                await this.fixEslintErrors();
                await this.gulpBuild();

                break;
            case UpgradeType.WithoutBaseConfig:
                this.logger(EslintUpgraderMessages.UpdateTypeWithoutBaseConfig);

                await this.addEslintSchematics();
                await this.esLintSchematicsRunModuleApp();

                if (this.libUpgrade) {
                    await this.esLintSchematicsRunModuleLib();
                }

                await this.uninstallDependencies();
                await this.installDependencies();
                await this.replaceEslintConfig();
                await this.replaceGulpLintFile();
                this.cleanUpPolyfills();
                this.cleanUpKarmaConfigFile();
                await this.fixEslintErrors();
                await this.gulpBuild();

                break;
            case UpgradeType.NoOperation:
                this.logger(EslintUpgraderMessages.UpdateTypeWithoutBoth);
                this.logger(EslintUpgraderMessages.ExitWithoutBaseConfig);
                this.logger(EslintUpgraderMessages.RCANoTsConfigFile);

                break;
            default:
                break;
        }

        this.logger(EslintUpgraderMessages.Completed);
    }

    constructor() {
        Common.rootPath = process.cwd();
        this.updateBasicPaths();
    }

    private updateBasicPaths(): void {
        this.tsconfigBaseFilePath = Common.rootPath + '\\' + this.tsconfigBaseFileName;
        this.tsconfigFilePath = Common.rootPath + '\\' + this.tsconfigFileName;
        this.angularFilePath = Common.rootPath + '\\' + this.angularFileName;
    }

    private checkLibUpgrade(): void {
        const angularFilePathExists = pathExists(this.angularFilePath);
        if (!angularFilePathExists) {
            return;
        }

        const angularFileContent = Common.readFileData(this.angularFilePath);
        this.libUpgrade = !!angularFileContent.includes('module-lib');
    }

    private checkFileExists(): UpgradeType {
        const tsconfigBaseFilePathExists = pathExists(this.tsconfigBaseFilePath);
        const tsconfigFilePathExists = pathExists(this.tsconfigFilePath);
        if (tsconfigBaseFilePathExists && tsconfigFilePathExists) {
            return UpgradeType.WithBaseConfig;
        } else if (tsconfigFilePathExists) {
            return UpgradeType.WithoutBaseConfig;
        } else {
            return UpgradeType.NoOperation;
        }
    }

    private updateTsConfigContent(revert = false) {
        revert ? this.logger(EslintUpgraderMessages.RevertTsConfigContent) : this.logger(EslintUpgraderMessages.UpdateTsConfigContent);

        revert ?
            this.updateFile(this.tsconfigFilePath, `// "extends": "./${this.tsconfigBaseFileName}"`, `"extends": "./${this.tsconfigBaseFileName}"`) :
            this.updateFile(this.tsconfigFilePath, `"extends": "./${this.tsconfigBaseFileName}"`, `// "extends": "./${this.tsconfigBaseFileName}"`);
    }

    private updateFile(filePath: string, prev: string, target: string): void {
        this.logger('Updating file...' + filePath + ',' + prev + ',' + target + '...');

        let fileData = Common.readFileData(filePath);
        fileData = fileData.replace(prev, target);
        fse.writeFileSync(filePath, fileData);
    }

    private renameTsBaseConfig(revert = false): void {
        revert ? this.logger(EslintUpgraderMessages.RenameTsBaseConfig) : this.logger(EslintUpgraderMessages.RevertTsBaseConfig);

        const cmd = revert ? `git mv ./${this.tsconfigBaseFileNameBackUp} ./${this.tsconfigBaseFileName}` : `git mv ./${this.tsconfigBaseFileName} ./${this.tsconfigBaseFileNameBackUp}`;
        this.runCommand(cmd);
    }

    private async addEslintSchematics(): Promise<void> {
        const cmd = 'ng add @angular-eslint/schematics --skip-confirmation';

        this.logger(cmd);
        await this.runCommand(cmd);
    }

    private async esLintSchematicsRunModuleApp(): Promise<void> {
        const cmd = 'ng g @angular-eslint/schematics:convert-tslint-to-eslint module-app';

        this.logger(cmd);
        await this.runCommand(cmd);
    }

    private async esLintSchematicsRunModuleLib(): Promise<void> {
        const cmd = 'ng g @angular-eslint/schematics:convert-tslint-to-eslint module-lib';

        this.logger(cmd);
        await this.runCommand(cmd);
    }

    /**
     * It updates to the eslintrc.json file on the root of the project.
     */
    public async replaceEslintConfig(): Promise<void> {
        const formattedNewContent = JSON.stringify(this.eslintConfigFileContent, null, 2)
            .replace('}', '}\n')

        this.logger(EslintUpgraderMessages.UpdateEslintConfig);

        if (fse.existsSync(`./${this.eslintConfigFileName}`)) {
            this.logger('Updating eslint file');
            Common.writeFileData(`./${this.eslintConfigFileName}`, formattedNewContent);
        } else {
            this.logger('Creating new eslint file');
            Common.writeFile(`./${this.eslintConfigFileName}`, formattedNewContent);
        }

        // race condition for the eslintrc file not being released by the writeFile function.
        await this.sleep(1000);
    }

    private async uninstallDependencies(): Promise<void> {
        const cmd = 'npm uninstall codelyzer gulp-tslint rxjs-tslint rxjs-tslint-rules tslint tslint-consistent-codestyle tslint-eslint-rules tslint-microsoft-contrib';

        this.logger(cmd);
        await this.runCommand(cmd);

        const removeForRollBackCmd = 'npm uninstall gulp-eslint eslint eslint-plugin-angular eslint-plugin-import eslint-plugin-jsdoc eslint-plugin-jsonc eslint-plugin-prefer-arrow ' +
            '@typescript-eslint/eslint-plugin @typescript-eslint/parser @angular-eslint/builder @angular-eslint/eslint-plugin @angular-eslint/eslint-plugin-template @angular-eslint/schematics ' +
            '@angular-eslint/template-parser'

        this.logger(removeForRollBackCmd);
        await this.runCommand(removeForRollBackCmd);
    }

    private async installDependencies(): Promise<void> {
        const cmd = 'npm install gulp-eslint@6.0.0 eslint@7.6.0 eslint-plugin-angular@4.1.0 eslint-plugin-import@2.21.0 eslint-plugin-jsdoc@36.1.1 eslint-plugin-jsonc@2.8.0 eslint-plugin-prefer-arrow@1.2.3 ' +
            '@typescript-eslint/eslint-plugin@4.16.1 @typescript-eslint/parser@4.16.1 @angular-eslint/builder@4.3.1 @angular-eslint/eslint-plugin@4.3.1 @angular-eslint/eslint-plugin-template@4.3.1 ' +
            '@angular-eslint/schematics@4.3.1 @angular-eslint/template-parser@4.3.1 --save-dev';

        this.logger(cmd);
        await this.runCommand(cmd);
    }

    private async fixEslintErrors(): Promise<void> {
        const rootFileLinterFix = 'npx eslint . --fix';
        this.logger(rootFileLinterFix);
        await this.runCommand(rootFileLinterFix, true, EslintUpgraderMessages.FinalizeInfoLintFix);

        const projectLinterFix = 'npx ng lint --fix';
        this.logger(projectLinterFix);
        await this.runCommand(projectLinterFix, true, EslintUpgraderMessages.FinalizeInfoLintFix);
    }

    private async replaceGulpLintFile(): Promise<void> {
        const cmd = `del ${this.targetGulpLintFilePath}`;

        this.logger(cmd);
        await this.runCommand(cmd);

        if (pathExists(this.targetGulpLintIndexFilePath)) {
            this.updateFile(
                this.targetGulpLintIndexFilePath,
                `import { LintModule } from './common/lint'`,
                `import { LintModule } from '../node_modules/@microsoft/windows-admin-center-sdk/tools/code-formatter/lint'`);
        }

        if (pathExists(this.targetGulpLintUiTestAutomationFilePath)) {
            this.updateFile(
                this.targetGulpLintUiTestAutomationFilePath,
                `import { LintModule } from './lint'`,
                `import { LintModule } from '../../node_modules/@microsoft/windows-admin-center-sdk/tools/code-formatter/lint'`);

            // The new UI automation framework will remove this line.
            // This is for the repositories that has not yet onboraded to the new frawework.
            this.updateFile(
                this.targetGulpLintUiTestAutomationFilePath,
                `export const uiTestAutomation = series(LintModule.lintUiTestAutomation, buildUiTestAutomation);`,
                `export const uiTestAutomation = series(LintModule.lintUiTestAutomation_legacy, buildUiTestAutomation);`);
        }
    }

    private async gulpBuild(): Promise<void> {
        const cmd = 'gulp build';
        this.logger(cmd);
        await this.runCommand(cmd);
    }

    private cleanUpPolyfills(): void {
        this.logger(EslintUpgraderMessages.UpdatePolyfills);

        const fileDataInLines = Common.readFileData(this.polifillsFilePath).split('\n');
        const newLines = fileDataInLines.filter(line => !line.includes(this.eslintDisable));
        Common.writeFileData(this.polifillsFilePath, newLines.join('\n'));
    }

    private cleanUpKarmaConfigFile(): void {
        this.logger(EslintUpgraderMessages.UpdateKarmaFile);

        this.updateFile(
            this.karmaConfigFilePath,
            `module.exports = (config) => {`,
            `module.exports = function (config) {`);
    }

    private runCommand(cmd: string, forceDebug = false, finalizeInfo = undefined): Promise<void> {
        console.log(`Running ${cmd}`);
        return new Promise((resolve) => {
            const child = exec(cmd, (error, stdout, stderr) => {
                if (error) {
                    console.error(`  exec error: ${error}`);
                    return;
                }

                stdout.split('\n').filter(line => line.trimEnd()).forEach(line => console.log(line));
                stderr.split('\n').filter(line => line.trimEnd()).forEach(line => console.error(line));

                resolve();
            });

            if (this.debug || forceDebug) {
                child.stdout.on('data', (data) => {
                    data.split('\n').filter(line => line.trimEnd()).forEach(line => console.log(`  ${line}`));
                });
            }

            child.stderr.on('data', (data) => {
                data.split('\n').filter(line => line.trimEnd()).forEach(line => console.error(`  ${line}`));
            });

            child.on('close', (code) => {
                console.log(`exitCode: ${code}`);
                if (code !== 0 && finalizeInfo) {
                    this.logger(finalizeInfo);
                    this.logger(EslintUpgraderMessages.Completed);
                }
            });
        });
    }

    private logger(value: EslintUpgraderMessages | string, prefix = 'Main thread') {
        console.log(`${prefix} - ${value}`);
    }

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
